import React, { useEffect, useMemo, useRef, useState } from 'react';
import './ReviewPanel.css';
import { createPortal } from 'react-dom';
import { CircleAlert, CircleCheck, Globe, Hourglass, PenLine, ShieldAlert, ShieldCheck, Sparkles, Users, X } from 'lucide-react';
import { Button } from '../ui';
import { findingMeta, severityLabel } from './findings';

/**
 * Resultado da revisão por IA (3b): o texto com os trechos marcados à esquerda e os pontos à direita.
 * Grave bloqueia até corrigir; Atenção aceita "Não é problema" com justificativa (vai para um admin).
 */
export function ReviewPanel({
  article, review, busy, onClose, onUseSuggestion, onEditExcerpt, onDismiss, onReopen, onPublishPublic, onPublishMembers,
}) {
  const findings = review?.findings || [];
  const open = findings.filter(f => f.state === 'OPEN');
  const resolved = findings.length - open.length;
  const pending = review?.status === 'PENDING';
  const rejected = review?.status === 'REJECTED';
  const clean = findings.length === 0;
  const canPublish = !busy && open.length === 0 && !pending;
  const markRefs = useRef({});
  const [active, setActive] = useState(null);

  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  const focus = (f) => {
    setActive(f.id);
    markRefs.current[f.id]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const title = pending ? 'Aguardando um admin'
    : rejected ? 'Um admin pediu ajuste'
      : clean ? 'Nada encontrado'
        : open.length === 0 ? 'Pronto para publicar' : 'Ainda não dá para publicar';

  return createPortal(
    <div className="rvp-backdrop" role="presentation" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="rvp" role="dialog" aria-modal="true" aria-label="Revisão por IA">
        <button type="button" className="rvp-close ss-iconbtn" onClick={onClose} aria-label="Fechar"><X size={18} aria-hidden="true" /></button>
        <div className="rvp-text">
          <h2>{article.title || 'Sem título'}</h2>
          {article.subtitle && <p className="rvp-text__sub"><Marked text={article.subtitle} field="subtitle" findings={findings} refs={markRefs} active={active} /></p>}
          <div className="rvp-text__body">
            <Marked text={article.bodyMd} field="body" findings={findings} refs={markRefs} active={active} paragraphs />
          </div>
        </div>

        <aside className="rvp-side">
          <header className="rvp-head">
            <span className={`rvp-head__icon ${open.length || rejected ? 'is-bad' : pending ? 'is-wait' : 'is-ok'}`}>
              {pending ? <Hourglass size={18} aria-hidden="true" /> : open.length || rejected ? <ShieldAlert size={18} aria-hidden="true" /> : <ShieldCheck size={18} aria-hidden="true" />}
            </span>
            <span className="rvp-head__text">
              <strong>{title}</strong>
              <span>{clean ? 'A revisão não achou nada sensível.' : `${findings.length} ${findings.length === 1 ? 'ponto' : 'pontos'} · ${resolved} ${resolved === 1 ? 'resolvido' : 'resolvidos'}`}</span>
            </span>
          </header>
          {!clean && (
            <span className="rvp-progress"><span style={{ width: `${(resolved / findings.length) * 100}%` }} /></span>
          )}
          {rejected && review.reviewerComment && (
            <p className="rvp-banner rvp-banner--bad"><CircleAlert size={15} aria-hidden="true" /><span>{review.reviewerComment}</span></p>
          )}
          {pending && (
            <p className="rvp-banner rvp-banner--wait"><Hourglass size={15} aria-hidden="true" /><span>Você justificou {findings.filter(f => f.state === 'DISMISSED').length === 1 ? 'um ponto' : 'alguns pontos'}. Um admin confirma e o artigo sai como público; você recebe o aviso no sininho.</span></p>
          )}

          <ul className="rvp-list">
            {findings.map(f => (
              <FindingCard key={f.id} f={f} active={active === f.id} locked={pending || busy}
                           onFocus={() => focus(f)} onUse={() => onUseSuggestion(f)} onEdit={() => onEditExcerpt(f)}
                           onDismiss={(text) => onDismiss(f, text)} onReopen={() => onReopen(f)} />
            ))}
            {clean && (
              <li className="rvp-clean"><CircleCheck size={18} aria-hidden="true" />Nenhum cliente, dado interno, credencial ou pessoa no texto.</li>
            )}
          </ul>

          <footer className="rvp-foot">
            <div className="rvp-foot__actions">
              <Button icon={Globe} onClick={onPublishPublic} disabled={!canPublish} className="rvp-publish">
                {busy === 'publish' ? 'Revisando...' : 'Publicar como público'}
              </Button>
              <Button variant="outline" icon={Users} onClick={onPublishMembers} disabled={!!busy}>Só alunos</Button>
            </div>
            <span className="rvp-foot__hint">"Não é problema" envia para um admin confirmar antes de publicar.</span>
          </footer>
        </aside>
      </div>
    </div>,
    document.body,
  );
}

function FindingCard({ f, active, locked, onFocus, onUse, onEdit, onDismiss, onReopen }) {
  const meta = findingMeta(f);
  const [justifying, setJustifying] = useState(false);
  const [text, setText] = useState(f.justification || '');
  const grave = f.severity === 'GRAVE';
  const state = f.state;
  return (
    <li className={`rvp-card rvp-card--${grave ? 'grave' : 'atencao'} ${state !== 'OPEN' ? 'is-done' : ''} ${active ? 'is-active' : ''}`}>
      <button type="button" className="rvp-card__head" onClick={onFocus}>
        <meta.icon size={14} aria-hidden="true" />
        <span>{meta.label}</span>
        <span className="rvp-card__sev">{severityLabel(f.severity)}</span>
      </button>
      <div className="rvp-card__body">
        <p className="rvp-card__reason" onClick={onFocus}>{f.reason || `"${f.excerpt}"`}</p>
        {state === 'OPEN' && f.suggestion && (
          <p className="rvp-card__suggestion"><Sparkles size={13} aria-hidden="true" />Trocar por "{f.suggestion}"</p>
        )}
        {state === 'OPEN' && !f.suggestion && (
          <p className="rvp-card__suggestion"><Sparkles size={13} aria-hidden="true" />Remover o trecho</p>
        )}
        {state === 'FIXED' && (
          <p className="rvp-card__done"><CircleCheck size={14} aria-hidden="true" />Corrigido
            {!locked && <button type="button" onClick={onReopen}>desfazer</button>}</p>
        )}
        {state === 'DISMISSED' && (
          <p className="rvp-card__done rvp-card__done--why"><PenLine size={13} aria-hidden="true" /><span>Não é problema: {f.justification}</span>
            {!locked && <button type="button" onClick={onReopen}>desfazer</button>}</p>
        )}
        {state === 'OPEN' && !justifying && (
          <div className="rvp-card__actions">
            <Button size="sm" onClick={onUse} disabled={locked}>{f.suggestion ? 'Usar sugestão' : 'Remover'}</Button>
            <Button size="sm" variant="outline" onClick={onEdit} disabled={locked}>Editar trecho</Button>
            {!grave && <button type="button" className="rvp-card__ghost" onClick={() => setJustifying(true)} disabled={locked}>Não é problema</button>}
          </div>
        )}
        {state === 'OPEN' && justifying && (
          <div className="rvp-card__why">
            <textarea className="ss-input" rows={2} value={text} onChange={(e) => setText(e.target.value)} maxLength={500}
                      placeholder="Por que não é problema? Ex.: é tempo de build, não identifica cliente." autoFocus />
            <div className="rvp-card__actions">
              <Button size="sm" onClick={() => onDismiss(text)} disabled={locked || text.trim().length < 5}>Enviar justificativa</Button>
              <button type="button" className="rvp-card__ghost" onClick={() => setJustifying(false)}>Cancelar</button>
            </div>
          </div>
        )}
      </div>
    </li>
  );
}

/** Texto com os trechos dos pontos marcados (grave em vermelho, atenção em âmbar). */
function Marked({ text, field, findings, refs, active, paragraphs = false }) {
  const parts = useMemo(() => {
    const value = text || '';
    const marks = [];
    findings.filter(f => f.state !== 'FIXED' && (f.field || 'body') === field).forEach(f => {
      let start = Number.isInteger(f.start) && value.slice(f.start, f.end) === f.excerpt ? f.start : value.indexOf(f.excerpt);
      if (start < 0) start = value.toLowerCase().indexOf((f.excerpt || '').toLowerCase());
      if (start < 0 || !f.excerpt) return;
      const end = start + f.excerpt.length;
      if (marks.some(m => start < m.end && end > m.start)) return;
      marks.push({ start, end, f });
    });
    marks.sort((a, b) => a.start - b.start);
    const out = [];
    let at = 0;
    marks.forEach(m => {
      if (m.start > at) out.push({ text: value.slice(at, m.start) });
      out.push({ text: value.slice(m.start, m.end), f: m.f });
      at = m.end;
    });
    if (at < value.length) out.push({ text: value.slice(at) });
    return out;
  }, [text, field, findings]);

  const render = (list) => list.map((p, i) => (p.f ? (
    <mark key={i} ref={el => { refs.current[p.f.id] = el; }}
          className={`rvp-mark rvp-mark--${p.f.severity === 'GRAVE' ? 'grave' : 'atencao'} ${p.f.state === 'DISMISSED' ? 'is-dismissed' : ''} ${active === p.f.id ? 'is-active' : ''}`}>
      {p.text}
    </mark>
  ) : <React.Fragment key={i}>{p.text}</React.Fragment>));

  if (!paragraphs) return <>{render(parts)}</>;
  return <div className="rvp-paras">{render(parts)}</div>;
}
